/**
 * Role model of the SPA — the client-side half of the RBAC that T.B1 enforces
 * server-side (docs/06-tasks.md T.B1). Nothing here ever *grants* anything: a
 * missing role degrades to the narrowest reading (fail-closed), because the
 * authority is the API's 403, not this file.
 */

/** `UserRole` of the contract (packages/shared, openapi `UserRole`). Repeated
 *  here so the SPA does not need the generated openapi module in the bundle. */
export type Role = 'ADMIN' | 'MANAGER' | 'VIEWER' | 'SUPPORT';

/**
 * Higher number = more power. Precedence is frozen: SUPPORT > ADMIN > MANAGER >
 * VIEWER (T.Z155 — SUPPORT sits ABOVE ADMIN so every `minRole` gate up to
 * `ADMIN` opens for it; the SPA has no `minRole="SUPPORT"` and must never grow
 * one). The rank is READ-side only: the api refuses every write a SUPPORT
 * session makes outside the exports (the central `SUPPORT_READ_ONLY` gate),
 * and this file mirrors that by never granting a write — nothing here flips a
 * button on for SUPPORT, because the owner asked for NO read-only UI mode: the
 * api is the one that says no.
 */
export const ROLE_RANK: Record<Role, number> = {
  VIEWER: 1,
  MANAGER: 2,
  ADMIN: 3,
  SUPPORT: 4,
};

/** Accepts the serialised enum (and a lower-cased variant of it), nothing else. */
export function parseRole(value: unknown): Role | undefined {
  if (typeof value !== 'string') return undefined;
  const candidate = value.trim().toUpperCase();
  return candidate === 'ADMIN' || candidate === 'MANAGER' || candidate === 'VIEWER' || candidate === 'SUPPORT'
    ? candidate
    : undefined;
}

/**
 * True when `role` is at least `min`. An absent / unparseable role is NEVER at
 * least anything — that is what keeps an unknown role read-only.
 */
export function hasAtLeast(role: unknown, min: Role): boolean {
  const parsed = parseRole(role);
  return parsed !== undefined && ROLE_RANK[parsed] >= ROLE_RANK[min];
}

/**
 * Human-readable label for the user chip / role editors (RU — T.R1).
 *
 * WAVE 10 (D21): the VIEWER label is «Куратор» — the single-role model gives every
 * account exactly one role, and a VIEWER holds the curator cabinet, not a passive
 * "observer" seat. The ENUM VALUE stays `VIEWER` (renaming it is a contract break);
 * this is a UI label only, and it deliberately does not touch `x-contract-role`.
 */
export function roleLabel(role: Role): string {
  switch (role) {
    case 'ADMIN':
      return 'Администратор';
    case 'MANAGER':
      return 'Менеджер';
    case 'VIEWER':
      return 'Куратор';
    case 'SUPPORT':
      // T.Z155 — «Поддержка»: всё читает, ничего не меняет (отказ ТОЛЬКО на бэкенде).
      return 'Поддержка';
  }
}
